import { useCart } from "../context/CartContext";
import { useCurrency } from "../context/CurrencyContext";
import { useNavigate } from "react-router-dom";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetDescription,
} from "../components/ui/sheet";
import { Button } from "../components/ui/button";
import { Separator } from "../components/ui/separator";
import { Minus, Plus, Trash } from "@phosphor-icons/react";
import { useState } from "react";

export default function CartSheet({ open, onOpenChange }) {
  const { items, updateQuantity, removeItem, totalPrice } = useCart();
  const { formatPrice } = useCurrency();
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  const handleCheckout = () => {
    if (items.length === 0) return;
    setLoading(true);
    onOpenChange(false);
    navigate("/checkout");
    setLoading(false);
  };

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent
        side="right"
        className="bg-[#F5E6A0] border-l border-[#E01A23]/10 w-full sm:max-w-md flex flex-col"
      >
        <SheetHeader>
          <SheetTitle
            className="text-[#1A1A1A] text-2xl"
            style={{ fontFamily: "'Lazy Dog', cursive" }}
          >
            your cart
          </SheetTitle>
          <SheetDescription className="text-[#3a3a3a]">
            {items.length === 0
              ? "Your cart is empty"
              : `${items.length} item${items.length > 1 ? "s" : ""}`}
          </SheetDescription>
        </SheetHeader>

        <div className="flex-1 overflow-y-auto mt-6 space-y-4 pr-1">
          {items.map((item) => (
            <div
              key={item.id}
              data-testid={`cart-item-${item.id}`}
              className="flex gap-4"
            >
              <div className="w-20 h-20 rounded-lg overflow-hidden flex-shrink-0 bg-[#EBD988]">
                <img
                  src={item.image}
                  alt={item.name}
                  className="w-full h-full object-cover"
                />
              </div>
              <div className="flex-1 min-w-0">
                <h4
                  className="text-[#1A1A1A] text-sm font-medium truncate"
                  style={{ fontFamily: "'Courier New', Courier, monospace" }}
                >
                  {item.name}
                </h4>
                <p className="text-[#E01A23] text-sm mt-0.5">
                  {formatPrice(item.price)}
                </p>
                <div className="flex items-center gap-2 mt-2">
                  <button
                    data-testid={`decrease-qty-${item.id}`}
                    onClick={() => updateQuantity(item.id, item.quantity - 1)}
                    className="w-6 h-6 flex items-center justify-center rounded border border-[#D4C470] text-[#3a3a3a] hover:text-[#1A1A1A] hover:border-[#1A1A1A] transition-colors"
                  >
                    <Minus size={12} />
                  </button>
                  <span className="text-[#1A1A1A] text-xs w-6 text-center">
                    {item.quantity}
                  </span>
                  <button
                    data-testid={`increase-qty-${item.id}`}
                    onClick={() => updateQuantity(item.id, item.quantity + 1)}
                    className="w-6 h-6 flex items-center justify-center rounded border border-[#D4C470] text-[#3a3a3a] hover:text-[#1A1A1A] hover:border-[#1A1A1A] transition-colors"
                  >
                    <Plus size={12} />
                  </button>
                  <button
                    data-testid={`remove-item-${item.id}`}
                    onClick={() => removeItem(item.id)}
                    className="ml-auto text-[#555] hover:text-[#E01A23] transition-colors"
                  >
                    <Trash size={16} />
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>

        {items.length > 0 && (
          <div className="mt-auto pt-4">
            <Separator className="bg-[#E01A23]/10 mb-4" />
            <div className="flex justify-between items-center mb-4">
              <span className="text-[#3a3a3a] text-sm">Total</span>
              <span
                className="text-[#1A1A1A] text-xl"
                data-testid="cart-total"
                style={{ fontFamily: "'Lazy Dog', cursive" }}
              >
                {formatPrice(totalPrice)}
              </span>
            </div>
            <Button
              data-testid="checkout-button"
              onClick={handleCheckout}
              disabled={loading}
              className="w-full bg-[#E01A23] hover:bg-[#c4161e] text-white h-12 text-sm tracking-wider rounded-full transition-colors"
              style={{ fontFamily: "'Courier New', Courier, monospace" }}
            >
              {loading ? "processing..." : "checkout"}
            </Button>
          </div>
        )}
      </SheetContent>
    </Sheet>
  );
}
